import {
  type ForwardedRef,
  forwardRef,
  type PropsWithChildren,
  type SyntheticEvent,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from 'react'
import { Modal as PrimitiveModal } from '../../primitives/Modal/Modal'
import { useIsMobileVariant } from '../../theme/hooks/use-is-mobile-variant'
import { SwipeableDrawer, type SwipeableDrawerProps } from '../SwipeableDrawer'
import { ModalBase } from './Modal.Base'
import { type ModalProps, type ModalRef } from './types'

type Props = PropsWithChildren<
  Omit<ModalProps, 'children' | 'open'> & {
    open?: boolean
  } & Pick<
      SwipeableDrawerProps,
      /**
       * Only applicable on our mobile variant, performance improvement
       * or simpler to mach native toolbar solution like Receiver app
       *
       * @default false
       */
      'disableBackdropTransition'
    >
>

/*
 * Our Modal is an UI element to display additional content or options without navigating away from the current view.
 * For our `mobile` variant it is shown as a bottom (swipeable) drawer, and for the other variants its in the middle of the screen.
 *
 * Demos:
 * - [Modal](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-modal--docs)
 *
 */
export const Modal = forwardRef(
  (
    {
      children,
      keepMounted,
      width,
      height,
      maxWidth,
      dividers = ['top', 'bottom'],
      isSwipeable = false,
      swipeCloseMode,
      disableBackdropTransition = false,
      isCloseable = true,
      isFullscreen = false,
      open = false,
      onBack,
      onClose,
      ...props
    }: Props,
    ref: ForwardedRef<ModalRef>
  ) => {
    const [isOpen, setIsOpen] = useState(open)
    const isMobile = useIsMobileVariant()

    /**
     * Integration with the `open` prop
     * When change do not come from our imperative handler make sure to update the internal state `isOpen`
     */
    useLayoutEffect(() => {
      if (open) {
        setIsOpen(true)
      } else {
        setIsOpen(false)
      }
    }, [open])

    /**
     * Integration with the `ref` prop
     */
    useImperativeHandle(
      ref,
      () => ({
        get isOpen() {
          return isOpen
        },
        open() {
          setIsOpen(true)
        },
        close() {
          setIsOpen(false)
        },
      }),
      [isOpen]
    )

    const handleClose = (event: SyntheticEvent<object>, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (isCloseable) {
        onClose?.(event, reason)
      }
    }

    // Please note that MUI Modal will also inject some of the props passed to Modal to its first child in order to be able to keep focus
    // see `ModalInjectedByMui`.
    const modalProps = {
      ...props,
      children,
      // we never expose the divider on mobile, never!
      dividers,
      keepMounted,
      open: isOpen,
      ...(isFullscreen ? {} : { width, height, maxWidth }),
    }

    return !isMobile ? (
      <PrimitiveModal
        {...props}
        open={isOpen}
        onClose={onClose ? handleClose : undefined}
        isFullscreen={isFullscreen}
        keepMounted={keepMounted}
      >
        <ModalBase {...modalProps}>
          <>{children}</>
        </ModalBase>
      </PrimitiveModal>
    ) : (
      <SwipeableDrawer
        keepMounted={keepMounted}
        anchor="bottom"
        isSwipeable={isCloseable && isSwipeable}
        swipeCloseMode={swipeCloseMode}
        disableSwipeToOpen
        disableBackdropTransition={disableBackdropTransition}
        {...props}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={onClose ? handleClose : () => {}}
        isFullscreen={isFullscreen}
        hysteresis={0.24}
        minFlingVelocity={250}
        transitionDuration={{
          enter: 225,
          exit: 140,
        }}
      >
        <ModalBase {...modalProps}>
          <>{children}</>
        </ModalBase>
      </SwipeableDrawer>
    )
  }
)
